import { getFieldRegistryConfig } from "../../registry/map";
import { ReviewFieldProps } from "../../registry/types";
import {
  FormConfigApplicationStructureItem,
  FormConfigItem,
  FormItemType,
  FormPage,
} from "../../types/form";
import { Preferences } from "@smartsuite/types";

export const isReviewPage = (page: FormPage): boolean => page.page_type === "review";

export interface ReviewPageFieldItem {
  item: FormConfigItem;
  parentSectionSlug?: string;
}

export const getFormPageFieldItems = (page: FormPage): FormConfigItem[] => {
  return page.items.flatMap((item) => {
    if (item.type === FormItemType.section) {
      return (item.params.items ?? []).filter((child) => child.type === FormItemType.field);
    }

    return item.type === FormItemType.field ? [item] : [];
  });
};

export const getReviewPageFieldItems = (page: FormPage): ReviewPageFieldItem[] => {
  return page.items.flatMap((item) => {
    if (item.type === FormItemType.section) {
      return (item.params.items ?? [])
        .filter((child) => child.type === FormItemType.field)
        .map((child) => ({
          item: child,
          parentSectionSlug: item.slug,
        }));
    }

    return item.type === FormItemType.field ? [{ item }] : [];
  });
};

export const filterItemsByAllowedFieldSlugs = (
  items: FormConfigItem[],
  allowedFieldSlugs: Set<string>
): FormConfigItem[] => {
  return items.reduce<FormConfigItem[]>((acc, item) => {
    if (item.type === FormItemType.section) {
      const filteredSectionItems = (item.params.items ?? []).filter((child) => {
        if (child.type !== FormItemType.field) return true;
        return allowedFieldSlugs.has(child.slug);
      });

      if (filteredSectionItems.length > 0) {
        acc.push({
          ...item,
          params: {
            ...item.params,
            items: filteredSectionItems,
          },
        });
      }

      return acc;
    }

    if (item.type !== FormItemType.field || allowedFieldSlugs.has(item.slug)) {
      acc.push(item);
    }

    return acc;
  }, []);
};

export const getReviewFieldSlugs = (page: FormPage): string[] => {
  const fieldSlugs = page.review?.field_slugs;
  return Array.isArray(fieldSlugs) ? fieldSlugs : [];
};

export const getReviewFieldLabel = (
  formItem: FormConfigItem,
  field?: FormConfigApplicationStructureItem
): string => {
  return formItem.params.label ?? field?.label ?? formItem.slug;
};

export const formatReviewFieldValue = ({
  appField,
  preferences,
  unansweredLabel,
  value,
}: {
  appField?: FormConfigApplicationStructureItem;
  preferences?: Preferences;
  unansweredLabel: string;
  value: unknown;
}): string => {
  if (!appField) return unansweredLabel;

  const registryConfig = getFieldRegistryConfig(appField.field_type);
  if (registryConfig.toReviewString) {
    const registryValue = registryConfig.toReviewString(value, appField, preferences);

    if (registryValue) {
      return registryValue;
    }
  }

  return unansweredLabel;
};

/**
 * The field's own review renderer, when it has one. Only the checklist does today — every other
 * field's value is a single line of text.
 */
export const getReviewFieldControl = (
  appField?: FormConfigApplicationStructureItem
): React.ComponentType<ReviewFieldProps> | undefined => {
  if (!appField) return undefined;

  return getFieldRegistryConfig(appField.field_type).reviewControl;
};
